import { BarChart3, FileText, LinkIcon, Target, Users } from 'lucide-react';

import { Card, CardContent } from '@/components/ui/card';
import { CoverageMentionSchema } from '@/services/types/coverage-types';

import { ArtifactTypeBreakdown } from './ArtifactTypeBreakdown';
import { CategoryBreakdown } from './CategoryBreakdown';
import { RecentMentions } from './RecentMentions';
import { StatusSummaryCard } from './StatusSummaryCard';

interface MentionsOverviewProps {
  mentions: CoverageMentionSchema[];
  onMentionSelect: (mentionId: string) => void;
}

export const MentionsOverview = ({
  mentions,
  onMentionSelect,
}: MentionsOverviewProps) => {
  // Calculate summary statistics
  const statusCounts = mentions.reduce(
    (acc, mention) => {
      const status = mention.status || 'UNKNOWN';
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>,
  );

  const categoryCounts = mentions.reduce(
    (acc, mention) => {
      const category = mention.extraction?.category || 'UNKNOWN';
      acc[category] = (acc[category] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>,
  );

  const artifactTypeCounts = mentions.reduce(
    (acc, mention) => {
      const type = mention.artifact_type || 'UNKNOWN';
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>,
  );

  const validMentions = mentions.filter(
    (m) => m.validation?.is_valid_coverage_mention,
  );
  const highConfidenceMentions = mentions.filter(
    (m) =>
      (m.extraction?.confidence || 0) > 0.8 ||
      (m.validation?.coverage_specificity_score || 0) > 0.7,
  );

  const linkedMentions = mentions.filter((m) => m.coverage_id !== null);
  const unlinkedMentions = mentions.filter((m) => m.coverage_id === null);

  return (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-600" />
              <div>
                <p className="text-2xl font-bold">{mentions.length}</p>
                <p className="text-sm text-gray-600">Total Mentions</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <Target className="h-5 w-5 text-green-600" />
              <div>
                <p className="text-2xl font-bold">{validMentions.length}</p>
                <p className="text-sm text-gray-600">Valid Mentions</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-purple-600" />
              <div>
                <p className="text-2xl font-bold">
                  {highConfidenceMentions.length}
                </p>
                <p className="text-sm text-gray-600">High Confidence</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <LinkIcon className="h-5 w-5 text-emerald-600" />
              <div>
                <p className="text-2xl font-bold text-emerald-600">
                  {linkedMentions.length}
                </p>
                <p className="text-sm text-gray-600">Linked to Coverage</p>
                <p className="text-xs text-gray-500">
                  {mentions.length > 0
                    ? Math.round(
                        (linkedMentions.length / mentions.length) * 100,
                      )
                    : 0}
                  % of total
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-orange-600" />
              <div>
                <p className="text-2xl font-bold">
                  {Object.keys(artifactTypeCounts).length}
                </p>
                <p className="text-sm text-gray-600">Artifact Types</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Status Breakdown */}
      <StatusSummaryCard
        statusCounts={statusCounts}
        totalMentions={mentions.length}
      />

      {/* Breakdowns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <CategoryBreakdown categoryCounts={categoryCounts} />
        <ArtifactTypeBreakdown artifactTypeCounts={artifactTypeCounts} />
      </div>

      {/* Recent Mentions */}
      <RecentMentions mentions={mentions} onMentionSelect={onMentionSelect} />
    </div>
  );
};
